import React from "react";
import { Link } from "@inertiajs/react";
import styled from "styled-components";
import {
  brand_orange_deep,
  brand_gradient,
  card_shadow_md,
  outerContainerCss,
  narrowScreenMediaQuery,
} from "../constants/styles";

function PortalHero({ user, subscription, completeness }) {
  if (!user) return null;

  const remaining = subscription?.remaining_days;
  const remainingState = remaining == null
    ? "none"
    : remaining > 30 ? "ok"
    : remaining > 7  ? "warn"
    : "danger";

  const completenessPct = typeof completeness?.percent === "number"
    ? Math.max(0, Math.min(100, completeness.percent))
    : null;

  return (
    <Band>
      <Inner>
        <Left>
          <Avatar
            $src={user.avatar_url || null}
            $hasImage={!!user.avatar_url}
          >
            {!user.avatar_url && (
              <AvatarFallback aria-hidden="true">
                {(user.name || user.email || "?").trim().charAt(0).toUpperCase()}
              </AvatarFallback>
            )}
          </Avatar>
          <NameBlock>
            <NameRow>
              <Name>{user.name || user.username || user.email}</Name>
              {user.role_label && <RoleBadge>{user.role_label}</RoleBadge>}
            </NameRow>
            {user.email && <Email>{user.email}</Email>}
          </NameBlock>
        </Left>

        <Right>
          {remaining != null && (
            <SubscriptionPill $state={remainingState}>
              <SubscriptionLabel>Membership</SubscriptionLabel>
              <SubscriptionDays>
                {remaining > 0 ? `${remaining} hari` : "Belum aktif"}
              </SubscriptionDays>
            </SubscriptionPill>
          )}
          {completenessPct != null && completenessPct < 100 && (
            <CompletenessRing
              as={completeness.cta_url ? Link : "span"}
              href={completeness.cta_url || undefined}
              title={`Kelengkapan data ${completenessPct}%`}
            >
              <RingSvg viewBox="0 0 36 36">
                <RingBg d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                <RingFill
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  style={{ strokeDasharray: `${completenessPct}, 100` }}
                />
              </RingSvg>
              <RingPct>{completenessPct}%</RingPct>
            </CompletenessRing>
          )}
        </Right>
      </Inner>
    </Band>
  );
}

export default React.memo(PortalHero);

const Band = styled.section`
  background:
    linear-gradient(90deg, ${brand_orange_deep} 0%, ${brand_orange_deep} 4px, transparent 4px),
    radial-gradient(120% 80% at 12% 0%, rgba(255, 255, 255, 0.85), transparent 55%),
    linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
  padding: 20px 0 18px;

  @media ${narrowScreenMediaQuery} {
    padding: 14px 0 12px;
  }

  [data-theme="dark"] & {
    background:
      linear-gradient(90deg, ${brand_orange_deep} 0%, ${brand_orange_deep} 4px, transparent 4px),
      radial-gradient(120% 80% at 12% 0%, rgba(255, 255, 255, 0.04), transparent 55%),
      linear-gradient(135deg, #1f2937 0%, #0f172a 100%);
  }
`;

const Inner = styled.div`
  ${outerContainerCss}
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;

  @media ${narrowScreenMediaQuery} {
    flex-wrap: wrap;
    gap: 12px;
  }
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
  flex: 1;

  @media ${narrowScreenMediaQuery} {
    gap: 10px;
  }
`;

const Avatar = styled.div`
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${({ $src, $hasImage }) =>
    $hasImage
      ? `center/cover no-repeat url("${$src}")`
      : "linear-gradient(135deg, #fcd34d, #fb923c)"};
  box-shadow: ${card_shadow_md};
  border: 2px solid #ffffff;
  position: relative;

  @media ${narrowScreenMediaQuery} {
    width: 44px;
    height: 44px;
  }
`;

const AvatarFallback = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.95);

  @media ${narrowScreenMediaQuery} { font-size: 18px; }
`;

const NameBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Name = styled.h1`
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  color: #1f2937;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media ${narrowScreenMediaQuery} { font-size: 16px; }
  [data-theme="dark"] & { color: #f3f4f6; }
`;

const RoleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  background: rgba(216, 89, 7, 0.12);
  color: ${brand_orange_deep};
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
  flex-shrink: 0;
`;

const Email = styled.span`
  font-size: 13px;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  [data-theme="dark"] & { color: #cbd5e1; }
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
`;

const SubscriptionPill = styled.div`
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 999px;
  background: ${({ $state }) =>
    $state === "ok"     ? "rgba(16, 185, 129, 0.12)" :
    $state === "warn"   ? "rgba(245, 158, 11, 0.16)" :
    $state === "danger" ? "rgba(220, 38, 38, 0.14)" :
                          "rgba(255, 255, 255, 0.65)"};
  border: 1px solid ${({ $state }) =>
    $state === "ok"     ? "rgba(16, 185, 129, 0.32)" :
    $state === "warn"   ? "rgba(245, 158, 11, 0.4)" :
    $state === "danger" ? "rgba(220, 38, 38, 0.32)" :
                          "rgba(216, 89, 7, 0.18)"};
`;

const SubscriptionLabel = styled.span`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7280;

  [data-theme="dark"] & { color: #9ca3af; }
`;

const SubscriptionDays = styled.span`
  font-size: 13px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: #1f2937;

  [data-theme="dark"] & { color: #f3f4f6; }
`;

const CompletenessRing = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
  cursor: ${({ href }) => (href ? "pointer" : "default")};

  &:hover { opacity: 0.85; }
`;

const RingSvg = styled.svg`
  width: 36px;
  height: 36px;
  transform: rotate(-90deg);
`;

const RingBg = styled.path`
  fill: none;
  stroke: rgba(216, 89, 7, 0.15);
  stroke-width: 3;

  [data-theme="dark"] & { stroke: rgba(255, 255, 255, 0.10); }
`;

const RingFill = styled.path`
  fill: none;
  stroke: ${brand_orange_deep};
  stroke-width: 3;
  stroke-linecap: round;
  stroke-dasharray: 0, 100;
  transition: stroke-dasharray 600ms ease;
`;

const RingPct = styled.span`
  font-size: 11px;
  font-weight: 800;
  color: ${brand_orange_deep};
  font-variant-numeric: tabular-nums;
`;
